import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../../api/axios";
import { toast } from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";

export const PROJECT_ACTIVITY_STATUSES = [
  "open",
  "in_progress",
  "completed",
  "on_hold",
];

function normNum(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export const ProjectActivityEditDialog = ({
  open,
  onClose,
  projectId,
  activity,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [budgetHours, setBudgetHours] = useState("");
  const [budgetFee, setBudgetFee] = useState("");
  const [isAdditional, setIsAdditional] = useState(false);
  const [status, setStatus] = useState("open");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (!open || !activity) return;
    setTitle(activity.title || "");
    setDescription(activity.description || "");
    setBudgetHours(
      activity.budget_hours != null && activity.budget_hours !== ""
        ? String(activity.budget_hours)
        : ""
    );
    setBudgetFee(
      activity.budget_fee != null && activity.budget_fee !== ""
        ? String(activity.budget_fee)
        : ""
    );
    setIsAdditional(!!activity.is_additional);
    setStatus(
      PROJECT_ACTIVITY_STATUSES.includes(activity.status)
        ? activity.status
        : "open"
    );
    setStartDate(activity.start_date || "");
    setEndDate(activity.end_date || "");
  }, [open, activity]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectId || !activity?.id) return;
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error(t("project.activities.title_required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: trimmed,
        description: description.trim() || null,
        budget_hours: normNum(budgetHours),
        budget_fee: normNum(budgetFee),
        is_additional: !!isAdditional,
        status,
        start_date: startDate || null,
        end_date: endDate || null,
      };
      const res = await axios.patch(
        `/projects/${projectId}/project_activities/${activity.id}`,
        { project_activity: payload }
      );
      if (!res.data?.success) {
        throw new Error((res.data?.errors || []).join(", "));
      }
      toast.success(t("project.activities.updated"));
      onSaved?.(res.data.data);
      onClose?.();
    } catch (err) {
      console.error(err);
      toast.error(t("project.activities.save_error"));
    } finally {
      setSaving(false);
    }
  };

  if (!activity) return null;

  const logged = Number(activity.logged_hours) || 0;
  const budgetH = activity.budget_hours != null ? Number(activity.budget_hours) : null;
  const hoursPct =
    budgetH != null && budgetH > 0 ? Math.min(100, (logged / budgetH) * 100) : null;

  const inputCls =
    "block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm";

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("project.activities.edit_dialog_title")}
      size="lg"
      closeOnOutsideClick={false}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2">
          <div className="text-sm font-medium text-gray-800">
            {t("project.activities.progress_section")}
          </div>
          <div className="text-sm text-gray-600">
            {t("project.activities.logged_vs_budget", {
              logged: logged.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }),
              budget:
                budgetH != null
                  ? budgetH.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })
                  : "—",
            })}
          </div>
          {hoursPct != null && (
            <>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full bg-indigo-600 transition-all"
                  style={{ width: `${hoursPct}%` }}
                />
              </div>
              <div className="text-xs text-gray-500">
                {hoursPct.toFixed(1)}% {t("project.activities.of_budget_hours")}
              </div>
            </>
          )}
          {activity.linked_work_orders_count != null && (
            <div className="text-sm text-gray-600">
              {t("project.activities.linked_work_orders", {
                count: activity.linked_work_orders_count,
              })}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t("project.activities.status_label")}
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={inputCls}
          >
            {PROJECT_ACTIVITY_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`project.activities.status.${s}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t("project.activities.title_field")}
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputCls}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t("project.activities.description_field")}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={inputCls}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t("project.activities.budget_hours")}
            </label>
            <input
              type="number"
              step="0.01"
              value={budgetHours}
              onChange={(e) => setBudgetHours(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t("project.activities.budget_fee")}
            </label>
            <input
              type="number"
              step="0.01"
              value={budgetFee}
              onChange={(e) => setBudgetFee(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t("project.activities.start_date")}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t("project.activities.end_date")}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="pa-edit-additional"
            type="checkbox"
            checked={isAdditional}
            onChange={(e) => setIsAdditional(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <label htmlFor="pa-edit-additional" className="text-sm text-gray-700">
            {t("project.activities.is_additional")}
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            {t("Cancel")}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? t("project.activities.saving") : t("project.activities.save")}
          </button>
        </div>
      </form>
    </PMDialog>
  );
};
